import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Trophy, ChartBar, Thermometer } from "@phosphor-icons/react";
import AppShell from "../components/AppShell";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
    { key: "standings", label: "Group Standings", icon: Trophy },
    { key: "fdr", label: "FDR Matrix", icon: Thermometer },
];

// 5-stop ramp used for both legacy integer (2..4) and new float (1.0..5.0)
// difficulty values from the market-aware FDR matrix.
const DIFF_COLOR_STOPS = [
    [1.0, "#7FE3B5"], // very easy = bright mint
    [2.0, "#C8E89D"], // easy = lime
    [3.0, "#F5E29C"], // mid = soft yellow
    [4.0, "#F0A878"], // hard = orange
    [5.0, "#F06060"], // very hard = soft red
];

function diffColor(value) {
    const v = Math.max(1.0, Math.min(5.0, Number(value) || 3.0));
    let lo = DIFF_COLOR_STOPS[0], hi = DIFF_COLOR_STOPS[DIFF_COLOR_STOPS.length - 1];
    for (let i = 0; i < DIFF_COLOR_STOPS.length - 1; i++) {
        if (v >= DIFF_COLOR_STOPS[i][0] && v <= DIFF_COLOR_STOPS[i + 1][0]) {
            lo = DIFF_COLOR_STOPS[i]; hi = DIFF_COLOR_STOPS[i + 1]; break;
        }
    }
    const t = (v - lo[0]) / Math.max(0.001, hi[0] - lo[0]);
    const hexToRgb = (h) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
    const lerp = (a, b) => Math.round(a + (b - a) * t);
    const [r1, g1, b1] = hexToRgb(lo[1]), [r2, g2, b2] = hexToRgb(hi[1]);
    const toHex = (n) => n.toString(16).padStart(2, "0");
    return "#" + toHex(lerp(r1, r2)) + toHex(lerp(g1, g2)) + toHex(lerp(b1, b2));
}
// Back-compat: code paths using `DIFF_COLOR[2|3|4]` keep working
const DIFF_COLOR = { 2: diffColor(2), 3: diffColor(3), 4: diffColor(4) };

// ---- Standings Tab ----------------------------------------------------------

function FormDots({ form }) {
    if (!form || form.length === 0) return <span className="text-white/20 text-[9px]">—</span>;
    return (
        <span className="inline-flex gap-0.5">
            {form.map((c, i) => (
                <span
                    key={i}
                    className="inline-block w-3.5 h-3.5 text-[8px] font-display font-black leading-[14px] text-center"
                    style={{
                        background: c === "W" ? "#9EE8B4" : c === "D" ? "#F5E29C" : "#F08080",
                        color: "#0d0d0d",
                    }}
                    title={c === "W" ? "Win" : c === "D" ? "Draw" : "Loss"}
                >
                    {c}
                </span>
            ))}
        </span>
    );
}

function StandingsTab() {
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        axios.get(`${API}/worldcup/standings`)
            .then((r) => setGroups(r.data.standings || []))
            .catch(() => toast.error("Standings unavailable — run /sync first"))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <div className="font-mono text-[12px] text-white/40">Loading standings…</div>;

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="wc-standings-grid">
            {groups.map((g) => (
                <div key={g.group} className="border border-white/10 bg-[#0d0d0d]" data-testid={`standings-${g.group}`}>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-fa-yellow px-3 pt-3 pb-2">
                        {(g.group || "").replace("GROUP_", "Group ")}
                    </div>
                    <table className="w-full font-mono text-[11px]">
                        <thead className="text-white/30 uppercase tracking-widest text-[9px]">
                            <tr>
                                <th className="text-left pl-3 pb-1">#</th>
                                <th className="text-left pb-1">Team</th>
                                <th className="text-center pb-1">P</th>
                                <th className="text-center pb-1">Form</th>
                                <th className="text-center pb-1">GD</th>
                                <th className="text-center pb-1">PTS</th>
                                <th className="text-right pr-3 pb-1">Adv%</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(g.table || []).map((r) => {
                                const adv = r.qualification?.advance_pct;
                                const advColor = adv >= 75 ? "#9EE8B4" : adv >= 40 ? "#F5E29C" : "#F08080";
                                return (
                                    <tr key={r.position} className="border-t border-white/5" data-testid={`row-${g.group}-${r.position}`}>
                                        <td className="pl-3 py-1.5 text-white/40 tabular-nums">{r.position}</td>
                                        <td className="py-1.5">
                                            <Link to={`/worldcup/team/${r.team_id}`} className="text-white hover:text-fa-yellow">
                                                {r.team_name}
                                            </Link>
                                        </td>
                                        <td className="text-center text-white/60 tabular-nums">{r.played ?? 0}</td>
                                        <td className="text-center">
                                            <FormDots form={r.form_chars || []} />
                                        </td>
                                        <td className={`text-center tabular-nums ${(r.goal_difference || 0) > 0 ? "text-fa-mint" : (r.goal_difference || 0) < 0 ? "text-[#FF6B6B]" : "text-white/40"}`}>
                                            {(r.goal_difference || 0) > 0 ? "+" : ""}{r.goal_difference ?? 0}
                                        </td>
                                        <td className="text-center text-fa-yellow font-display font-black tabular-nums">{r.points ?? 0}</td>
                                        <td className="text-right pr-3 font-display font-black tabular-nums" style={{ color: advColor }} data-testid={`adv-${r.team_id}`}>
                                            {adv != null ? `${adv}%` : "—"}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            ))}
        </div>
    );
}

// ---- FDR Matrix Tab ---------------------------------------------------------

function FdrTab() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        axios.get(`${API}/worldcup/fdr-matrix?matchdays=3`)
            .then((r) => setRows(r.data.rows || []))
            .catch(() => toast.error("FDR matrix unavailable"))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <div className="font-mono text-[12px] text-white/40">Loading FDR matrix…</div>;

    return (
        <div data-testid="fdr-matrix-section">
            <p className="font-mono text-[11px] text-white/60 leading-snug mb-3">
                Easiest first. Difficulty scale: <span style={{ color: diffColor(1) }}>1.0 = very easy</span> · <span style={{ color: diffColor(2.5) }}>2.5 = mid</span> · <span style={{ color: diffColor(5) }}>5.0 = very hard</span>. <span className="text-fa-mint">★</span> = market-derived (Betfair/SBOBET projections), otherwise tier-based fallback.
            </p>
            <div className="border border-white/10 bg-[#0d0d0d] overflow-x-auto" data-testid="fdr-table">
                <table className="w-full font-mono text-[11px]">
                    <thead>
                        <tr className="text-white/40 uppercase tracking-widest text-[9px] border-b border-white/10">
                            <th className="text-left p-2">#</th>
                            <th className="text-left p-2">Team</th>
                            <th className="text-left p-2">Group</th>
                            <th className="text-center p-2">MD1</th>
                            <th className="text-center p-2">MD2</th>
                            <th className="text-center p-2">MD3</th>
                            <th className="text-right p-2">Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r, i) => (
                            <tr key={r.team_id} className="border-t border-white/5 hover:bg-white/5" data-testid={`fdr-row-${r.team_id}`}>
                                <td className="p-2 text-white/30 tabular-nums">{i + 1}</td>
                                <td className="p-2">
                                    <Link to={`/worldcup/team/${r.team_id}`} className="text-white font-display font-black hover:text-fa-yellow flex items-center gap-1.5">
                                        {r.team_name}
                                        {r.has_market_data && (
                                            <span className="text-fa-mint text-[8px]" title="Market-derived projections available">★</span>
                                        )}
                                    </Link>
                                </td>
                                <td className="p-2 text-white/40 text-[9px]">{(r.group || "").replace("GROUP_", "")}</td>
                                {[0, 1, 2].map((idx) => {
                                    const f = r.fixtures[idx];
                                    if (!f) return <td key={idx} className="p-2 text-center text-white/20">—</td>;
                                    const color = diffColor(f.difficulty);
                                    const tipBits = [`${f.opponent} (${f.venue})`, `Difficulty ${f.difficulty}`];
                                    if (f.proj_goals_for != null) tipBits.push(`Proj goals for: ${f.proj_goals_for}`);
                                    if (f.proj_cs_pct != null) tipBits.push(`Clean sheet odds: ${f.proj_cs_pct}%`);
                                    if (f.opp_proj_goals != null) tipBits.push(`Opp xG: ${f.opp_proj_goals}`);
                                    return (
                                        <td key={idx} className="p-2 text-center" data-testid={`fdr-${r.team_id}-md${idx + 1}`}>
                                            <div
                                                className="inline-block px-2 py-1 font-display font-black text-[10px]"
                                                style={{
                                                    background: color + "26",
                                                    color: color,
                                                    border: `1px solid ${color}55`,
                                                }}
                                                title={tipBits.join("\n")}
                                            >
                                                {f.opponent.slice(0, 6).toUpperCase()} ({f.venue})
                                                {f.difficulty_source === "market" && <span className="ml-1 opacity-70">★</span>}
                                            </div>
                                        </td>
                                    );
                                })}
                                <td className="p-2 text-right font-display font-black text-fa-yellow tabular-nums">{r.total_difficulty}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ---- Page wrapper -----------------------------------------------------------

export default function WorldCupStandings() {
    const [tab, setTab] = useState("standings");

    return (
        <AppShell>
            <section className="space-y-5">
                <Link to="/worldcup" className="font-mono text-[10px] uppercase tracking-widest text-white/40 hover:text-white inline-flex items-center gap-1.5">
                    <ArrowLeft size={11} weight="bold" /> Back to WC Hub
                </Link>

                <header className="border-2 border-fa-mint/40 bg-[#0d0d0d] p-6" data-testid="wc-standings-header">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-mint flex items-center gap-2">
                        <ChartBar size={11} weight="fill" />
                        Group Stage Data
                    </div>
                    <h1 className="font-display font-black text-2xl md:text-3xl uppercase tracking-tight mt-2">
                        Live tables · FDR heat-map
                    </h1>
                </header>

                <div className="flex items-center gap-2 border-b border-white/10" data-testid="standings-tabs">
                    {TABS.map((t) => {
                        const Icon = t.icon;
                        const active = tab === t.key;
                        return (
                            <button
                                key={t.key}
                                type="button"
                                onClick={() => setTab(t.key)}
                                data-testid={`tab-${t.key}`}
                                className={`font-mono text-[11px] uppercase tracking-widest px-4 py-2.5 border-b-2 flex items-center gap-1.5 ${
                                    active
                                        ? "border-fa-mint text-fa-mint"
                                        : "border-transparent text-white/40 hover:text-white"
                                }`}
                            >
                                <Icon size={11} weight={active ? "fill" : "regular"} />
                                {t.label}
                            </button>
                        );
                    })}
                </div>

                {tab === "standings" && <StandingsTab />}
                {tab === "fdr" && <FdrTab />}
            </section>
        </AppShell>
    );
}
